import axios from 'axios'

import store from '../store'
import keycloak, { isSsoSession, clearSsoSession } from '../keycloak'
import { queryClient } from '../queryClient'
import { useUserStore, useSubscriptionStore } from '../state'

const authService = {
  login: async ({ email, password }: any) => {
    try {
      const {
        data: { token },
      } = await axios.post('/api/platform/auth/login', {
        email,
        password,
      })

      // This token is a legacy HS256 one — make sure no stale 'sso' marker
      // survives, or every SSO code path (token adoption, 401 retry with
      // keycloak.token) would treat this legacy session as Keycloak-issued.
      clearSsoSession()
      store.dispatch.auth.setToken(token)
      localStorage.setItem('token', token)
    } catch (error) {
      // alert(error.response.data.message)
      throw error
    }
  },
  // endSsoSession: true only for USER-INITIATED logout. Automatic teardown
  // (401 handler, refresh failure) passes false — those paths must never
  // escalate into a realm-wide Keycloak single logout.
  logout: (endSsoSession = true) => {
    // Capture before clearing: was this session issued by Keycloak?
    const wasSsoSession = isSsoSession()
    localStorage.removeItem('token')
    clearSsoSession()
    store.dispatch.layout.resetLayoutState()
    store.dispatch.profile.resetProfileState()
    store.dispatch.providerInfo.resetProviderState()
    store.dispatch.request.resetRequestState()
    // resetting the auth store will cause re-render in App.tsx so no navigation needed
    store.dispatch.auth.resetAuthState()
    // Full teardown of the caches too — every logout path funnels through
    // here. These used to be wiped only by useLogout or by the old
    // full-page redirect on the automatic 401 path; without this, the
    // previous user's React Query / zustand data (memory AND the persisted
    // copies) leaks into the next session in the same tab.
    queryClient.clear()
    useUserStore.getState().clearUser()
    useSubscriptionStore.getState().reset()
    localStorage.removeItem('user-storage')
    localStorage.removeItem('subscription-store')
    // End the Keycloak session too, otherwise the next login silently
    // re-authenticates via the still-valid SSO cookie. Gated on the session
    // SOURCE, not just keycloak.authenticated: a legacy session must stay
    // local even if a stray Keycloak cookie exists. didInitialize guards the
    // pre-init window where keycloak.logout would throw. Must stay the last
    // statement — it navigates away.
    if (
      endSsoSession &&
      wasSsoSession &&
      keycloak.didInitialize &&
      keycloak.authenticated
    ) {
      keycloak.logout({ redirectUri: window.location.origin })
    } else if (keycloak.didInitialize && keycloak.authenticated) {
      // Local-only teardown (automatic 401/refresh-failure path) or a legacy
      // logout with a stray Keycloak session in memory: stop keycloak-js'
      // refresh timer and drop its in-memory tokens. Without this, the next
      // auto-refresh fires onTokens into the now-empty localStorage and
      // silently re-adopts the session minutes after logout. tokenSource is
      // already cleared, so the onAuthLogout this triggers is a no-op.
      keycloak.clearToken()
    }
  },
}

export { authService }
